import React from 'react';
import { Divider } from '@/components/divider/Divider';
import TablzLogo from '../../../images/tablz-logo.svg';
import { TDR } from 'tdr-common';
import { analytics } from '@/helpers/analytics';
import { LINK_CLICKED_EVENT } from '@/helpers/analytics/constants';
import { getCdnImageUri } from '@/helpers/getCDNURI';

const TABLZ_URL =
	'https://tablz.com?utm_source=booking_portal&utm_medium=referral&utm_campaign=restaurant_portal_header&utm_content=logo';

type HeaderBrandSectionProps = {
	branding?: TDR.RestaurantSettings['branding'];
	restaurantInfo?: RestaurantInfo;
};

type RestaurantInfo = {
	restaurantId: TDR.Restaurant['id'];
	restaurantName: TDR.Restaurant['name'];
	restaurantDisplayName: TDR.Restaurant['displayName'];
};

function isSafeHttpUrl(raw: string): boolean {
	const trimmed = raw.trim();
	if (!trimmed) {
		return false;
	}
	try {
		const u = new URL(trimmed);
		return u.protocol === 'http:' || u.protocol === 'https:';
	}
	catch {
		return false;
	}
}

export const TablzLogoLink = ({ restaurantInfo }: { restaurantInfo?: RestaurantInfo }) => (
	<a
		href={TABLZ_URL}
		target="_blank"
		rel="noopener noreferrer"
		className="w-8 h-8 cursor-pointer hover:opacity-80 transition-opacity"
		onClick={() => {
			analytics.track(LINK_CLICKED_EVENT, {
				linkUrl: TABLZ_URL,
				linkText: 'Tablz Logo',
				linkLocation: 'Header',
				restaurantId: restaurantInfo?.restaurantId,
				restaurantName: restaurantInfo?.restaurantName
			});
		}}
	>
		<img src={TablzLogo} width="100%" height="100%" alt="Tablz" />
	</a>
);

const HeaderLogo = ({ branding, restaurantInfo }: { branding?: TDR.RestaurantSettings['branding'], restaurantInfo?: RestaurantInfo }) => {
	if (!branding?.header?.logoImagePath) {
		return <TablzLogoLink restaurantInfo={restaurantInfo} />;
	}

	const logoSrc = getCdnImageUri(branding.header.logoImagePath, {
		height: 70,
		width: 240,
		fit: 'contain'
	});

	const logoImg = (
		<img
			src={logoSrc}
			alt={restaurantInfo?.restaurantName || restaurantInfo?.restaurantId}
			className="h-full w-auto max-h-9 object-contain"
		/>
	);

	const stretchWrap = (child: React.ReactNode) => (
		<div className="flex self-stretch flex-shrink-0 items-center">{child}</div>
	);

	const logoUrl = branding.header.logoTargetURL;
	if (logoUrl && isSafeHttpUrl(logoUrl)) {
		return stretchWrap(
			<a
				href={logoUrl}
				target="_blank"
				rel="noopener noreferrer"
				className="flex h-full min-h-0 cursor-pointer items-center hover:opacity-80 transition-opacity"
			>
				{logoImg}
			</a>
		);
	}

	return stretchWrap(
		<div className="flex h-full min-h-0 items-center justify-center">{logoImg}</div>
	);
};

/**
 * Left rail: Tablz or restaurant branding logo, divider, and header title.
 */
export const HeaderBrandSection = ({ branding, restaurantInfo }: HeaderBrandSectionProps) => {
	const headerTitle = branding?.header?.title || restaurantInfo?.restaurantDisplayName;

	return (
		<div className="flex gap-3 min-h-[70px] items-center">
			<HeaderLogo branding={branding} restaurantInfo={restaurantInfo} />
			<div className="rotate-90 flex w-fit">
				<Divider width={20} />
			</div>
			<h1 className="text-zinc-100 font-lora text-[32px] inline-block font-medium m-0 items-center text-nowrap">
				{headerTitle}
			</h1>
		</div>
	);
};
